/**
 * Criação de despesa: outbox, como o roteiro.
 *
 * Registrar um jantar no restaurante sem sinal é o caso comum, não a exceção —
 * por isso a escrita vai para a fila local e o servidor recebe depois. O mesmo
 * UUIDv7 serve de id da despesa, id da mutação e `Idempotency-Key`: é o que
 * torna o reenvio seguro, porque o servidor reconhece a repetição.
 *
 * **A divisão é calculada aqui, com a mesma função do servidor.** Não é
 * duplicação: é o que permite mostrar "cada um paga X" antes de salvar, e
 * offline. O servidor recalcula e a sua conta é a que vale — se divergirem, é
 * bug, e há teste dos dois lados.
 */

import { type SplitMode, splitExpense, uuidv7 } from '@nv/core';
import { create } from 'zustand';

import { localDb } from '../roteiro/persistence';

export interface NovaDespesa {
  description: string;
  category: string;
  /** Sempre `trip`: despesa privada saiu do produto (13/09/2026, item 34). */
  scope: 'trip';
  subtotalMinor: number;
  taxMinor: number;
  tipMinor: number;
  currency: string;
  splitMode: SplitMode;
  spentAt: string;
  participantMemberIds: string[];
  payments: { payerMemberId: string; amountMinor: number }[];
  percentages?: Record<string, number>;
  amounts?: Record<string, number>;
}

export interface PreviaDaDivisao {
  totalMinor: number;
  shares: { participantId: string; total: number; balance: number }[];
  erro?: string;
}

/**
 * Prévia da divisão para a tela do passo 3.
 *
 * Devolve o erro em vez de lançar: enquanto a pessoa digita percentuais, a
 * soma passa por dezenas de estados inválidos, e cada um deles é normal.
 *
 * `familiaDe` mapeia `memberId` → `familyId` e só importa em `per_family`. Sem
 * ele, a prévia de `per_family` dividiria por pessoa enquanto o servidor
 * dividiria por família: os dois números apareceriam diferentes para a mesma
 * despesa, e o da tela seria o errado.
 */
export function preverDivisao(
  despesa: NovaDespesa,
  familiaDe: Readonly<Record<string, string | undefined>> = {},
): PreviaDaDivisao {
  try {
    const ordenados = [...new Set(despesa.participantMemberIds)].sort();
    const resultado = splitExpense({
      mode: despesa.splitMode,
      subtotal: despesa.subtotalMinor,
      tax: despesa.taxMinor,
      tip: despesa.tipMinor,
      participants: ordenados.map((id) => {
        const familyId = familiaDe[id];
        return familyId ? { id, familyId } : { id };
      }),
      payments: despesa.payments.map((p) => ({
        participantId: p.payerMemberId,
        amount: p.amountMinor,
      })),
      ...(despesa.percentages ? { percentages: despesa.percentages } : {}),
      ...(despesa.amounts ? { amounts: despesa.amounts } : {}),
    });

    return {
      totalMinor: resultado.total,
      shares: resultado.shares.map((s) => ({
        participantId: s.participantId,
        total: s.total,
        balance: s.balance,
      })),
    };
  } catch (erro) {
    return {
      totalMinor: despesa.subtotalMinor + despesa.taxMinor + despesa.tipMinor,
      shares: [],
      erro: erro instanceof Error ? erro.message : 'Divisão inválida.',
    };
  }
}

interface FinanceState {
  toast: string | null;
  limparToast: () => void;
  /** Enfileira a despesa. Devolve o id, para a tela navegar. */
  registrar: (tripId: string, despesa: NovaDespesa) => Promise<string>;
  /** Edição é substituição: a despesa passa a ser ISTO (item 32). */
  editar: (expenseId: string, baseVersion: number, despesa: NovaDespesa) => Promise<void>;
  excluir: (expenseId: string, baseVersion: number) => Promise<void>;
}

async function enfileirar(mutacao: { operation: 'update' | 'delete'; payload: unknown; baseVersion: number }) {
  const db = await localDb();
  await db.outbox.put({
    mutationId: uuidv7(),
    entity: 'expense',
    ...mutacao,
    createdAt: Date.now(),
    retryCount: 0,
    status: 'pending',
  });
  window.dispatchEvent(new CustomEvent('nv:outbox-changed'));
}

export const useFinanceStore = create<FinanceState>((set) => ({
  toast: null,
  limparToast: () => set({ toast: null }),

  registrar: async (tripId, despesa) => {
    void tripId;
    const id = uuidv7();
    const db = await localDb();

    await db.outbox.put({
      mutationId: id,
      entity: 'expense',
      operation: 'create',
      payload: { create: { id, ...despesa } },
      createdAt: Date.now(),
      retryCount: 0,
      status: 'pending',
    });

    // O badge de sincronização precisa saber que a fila mudou.
    window.dispatchEvent(new CustomEvent('nv:outbox-changed'));

    set({
      toast:
        navigator.onLine === false
          ? 'Despesa salva · pendente de sincronização'
          : 'Despesa salva',
    });

    return id;
  },

  editar: async (expenseId, baseVersion, despesa) => {
    // O cliente de sync manda `{ baseVersion, ...patch }` no PATCH: o patch
    // é a despesa inteira, como o servidor espera.
    await enfileirar({ operation: 'update', baseVersion, payload: { expenseId, patch: despesa } });
    set({ toast: navigator.onLine === false ? 'Despesa editada · pendente de sincronização' : 'Despesa editada' });
  },

  excluir: async (expenseId, baseVersion) => {
    await enfileirar({ operation: 'delete', baseVersion, payload: { expenseId } });
    set({ toast: navigator.onLine === false ? 'Despesa excluída · pendente de sincronização' : 'Despesa excluída' });
  },
}));
